<template>
  <div class="container-fluid mt-4" dir="rtl">
    
    <!-- 1. شريط الإحصائيات (Grid محسن) -->
    <div class="row g-3 mb-4">
      <div class="col-12 col-md-4">
        <div class="card bg-danger text-white shadow-sm h-100">
          <div class="card-body d-flex justify-content-between align-items-center">
            <div>
              <h3 class="mb-0 fw-bold">{{ stats.urgent }}</h3>
              <span class="small">بلاغات عاجلة (نشطة)</span>
            </div>
            <i class="fas fa-exclamation-triangle fa-2x opacity-50"></i>
          </div>
        </div>
      </div>
      <div class="col-6 col-md-4">
        <div class="card bg-primary text-white shadow-sm h-100">
          <div class="card-body d-flex justify-content-between align-items-center p-3">
            <div>
              <h3 class="mb-0 fw-bold">{{ stats.new }}</h3>
              <span class="small">بلاغات جديدة</span>
            </div>
            <i class="fas fa-inbox fa-2x opacity-50 d-none d-sm-block"></i>
          </div>
        </div>
      </div>
      <div class="col-6 col-md-4">
        <div class="card bg-light border text-dark shadow-sm h-100">
          <div class="card-body d-flex justify-content-between align-items-center p-3">
            <div>
              <h3 class="mb-0 fw-bold">{{ stats.total }}</h3>
              <span class="small">الإجمالي اليوم</span>
            </div>
            <i class="fas fa-list fa-2x opacity-25 d-none d-sm-block"></i>
          </div>
        </div>
      </div>
    </div>

    <!-- 2. أدوات التحكم (Responsive Flex) -->
    <div class="d-flex flex-column flex-md-row justify-content-between align-items-center mb-3 gap-3">
      <h4 class="fw-bold m-0"><i class="fas fa-headset ms-2 text-primary"></i> متابعة بلاغات اللجان</h4>
      
      <div class="d-flex gap-2 w-100 w-md-auto">
        <button @click="fetchComplaints" class="btn btn-dark flex-fill flex-md-grow-0" :disabled="loading">
          <i class="fas fa-sync-alt ms-1" :class="{'fa-spin': loading}"></i> <span class="d-none d-sm-inline">تحديث</span>
        </button>
        
        <select v-model="filterStatus" @change="fetchComplaints" class="form-select flex-fill flex-md-grow-0 w-auto">
          <option value="all">الكل</option>
          <option value="sent">جديد</option>
          <option value="viewing">جاري المتابعة</option>
          <option value="resolved">تم الحل</option>
        </select>
      </div>
    </div>

    <!-- 3. المحتوى -->
    
    <!-- A. عرض الجدول (يظهر فقط في الشاشات المتوسطة والكبيرة d-none d-md-block) -->
    <div class="card shadow-sm d-none d-md-block">
      <div class="table-responsive">
        <table class="table table-hover align-middle mb-0">
          <thead class="table-light">
            <tr>
              <th># اللجنة</th>
              <th>نوع البلاغ</th>
              <th>الأهمية</th>
              <th style="width: 40%">تفاصيل البلاغ</th>
              <th>التوقيت</th>
              <th>الحالة</th>
              <th>إجراءات</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="complaint in complaints" :key="'desk-'+complaint.id" :class="getRowClass(complaint)">
              <!-- نفس محتوى الجدول القديم -->
              <td>
                <div class="fw-bold text-nowrap">لجنة : {{ complaint.user ? truncate(complaint.user.committee_name, 20) : '---' }}</div>
                <small class="text-muted">{{ complaint.user ? complaint.user.name : '' }}</small>
              </td>
              <td>{{ getTypeText(complaint.type) }}</td>
              <td>
                <span class="badge" :class="getPriorityClass(complaint.priority)">{{ getPriorityText(complaint.priority) }}</span>
              </td>
              <td>
                 <!-- تفاصيل البلاغ والرد (نفس الكود السابق) -->
                 <div class="mb-2">
                    <p class="mb-0 text-dark">{{ complaint.description }}</p>
                 </div>
                 <div v-if="complaint.admin_response" class="mt-2 p-2 bg-white border rounded">
                    <strong class="text-success d-block small"><i class="fas fa-reply ms-1"></i> الرد:</strong>
                    <p class="mb-1 text-dark small">{{ complaint.admin_response }}</p>
                    <div class="text-end pt-1 border-top">
                      <small v-if="complaint.user_read_at" class="text-primary fw-bold"><i class="fas fa-check-double"></i> تمت القراءة {{ formatTime(complaint.user_read_at) }}</small>
                      <small v-else class="text-muted"><i class="fas fa-check"></i> تم الإرسال</small>
                    </div>
                 </div>
              </td>
              <td class="small text-muted text-nowrap" dir="ltr">{{ formatTime(complaint.created_at) }}</td>
              <td><span class="badge" :class="getStatusBadge(complaint.status)">{{ getStatusText(complaint.status) }}</span></td>
              <td>
                <div class="btn-group btn-group-sm">
                  <button v-if="complaint.status === 'sent'" @click="updateStatus(complaint, 'viewing')" class="btn btn-outline-primary"><i class="fas fa-eye"></i></button>
                  <button v-if="complaint.status !== 'resolved'" @click="updateStatus(complaint, 'resolved')" class="btn btn-outline-success"><i class="fas fa-check"></i></button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- B. عرض الكروت (يظهر فقط في الموبايل d-md-none) -->
    <div class="d-md-none">
        <div v-for="complaint in complaints" :key="'mob-'+complaint.id" class="card shadow-sm mb-3 border-0">
            <!-- رأس الكارت: الحالة واللجنة -->
            <div class="card-header d-flex justify-content-between align-items-center" :class="getRowClass(complaint) ? 'bg-danger-subtle' : 'bg-white'">
                <div class="fw-bold small text-truncate" style="max-width: 60%;">
                    {{ complaint.user ? complaint.user.committee_name : 'لجنة غير معروفة' }}
                </div>
                <span class="badge" :class="getStatusBadge(complaint.status)">{{ getStatusText(complaint.status) }}</span>
            </div>
            
            <div class="card-body">
                <!-- بادجات المعلومات -->
                <div class="d-flex gap-2 mb-2">
                    <span class="badge bg-light text-dark border">{{ getTypeText(complaint.type) }}</span>
                    <span class="badge" :class="getPriorityClass(complaint.priority)">{{ getPriorityText(complaint.priority) }}</span>
                    <span class="badge bg-light text-muted ms-auto" dir="ltr">{{ formatTime(complaint.created_at) }}</span>
                </div>

                <!-- نص الشكوى -->
                <p class="card-text fw-bold text-dark mb-3 bg-light p-2 rounded">
                    {{ complaint.description }}
                </p>

                <!-- الرد (إن وجد) -->
                <div v-if="complaint.admin_response" class="alert alert-success p-2 mb-3">
                    <small class="fw-bold d-block mb-1"><i class="fas fa-reply ms-1"></i> الرد:</small>
                    <div class="small">{{ complaint.admin_response }}</div>
                    <div class="text-end border-top border-success-subtle mt-1 pt-1">
                         <small v-if="complaint.user_read_at" class="fw-bold"><i class="fas fa-check-double"></i> قرأها الرئيس</small>
                         <small v-else><i class="fas fa-check"></i> لم تقرأ</small>
                    </div>
                </div>

                <!-- أزرار الإجراءات (عريضة للموبايل) -->
                <div class="d-flex gap-2">
                    <button v-if="complaint.status === 'sent'" @click="updateStatus(complaint, 'viewing')" class="btn btn-outline-primary btn-sm flex-fill">
                        <i class="fas fa-eye ms-1"></i> متابعة
                    </button>
                    <button v-if="complaint.status !== 'resolved'" @click="updateStatus(complaint, 'resolved')" class="btn btn-success btn-sm flex-fill">
                        <i class="fas fa-check ms-1"></i> إنهاء وحل
                    </button>
                </div>
            </div>
        </div>
    </div>

    <!-- رسالة فارغة -->
    <div v-if="complaints.length === 0" class="text-center py-5 text-muted">
        <i class="fas fa-inbox fa-3x mb-3 opacity-25"></i>
        <p>لا توجد بلاغات مطابقة لعملية البحث.</p>
    </div>

  </div>
</template>

<script>
import api from '@/api/axios';

export default {
  // ... (نفس الـ Script السابق تماماً) ...
  // فقط قم بنقل الدوال والـ data كما هي
  data() {
    return {
      loading: false,
      complaints: [],
      stats: { total: 0, urgent: 0, new: 0 },
      filterStatus: 'all',
      polling: null 
    };
  },
  mounted() {
    this.fetchComplaints();
    this.polling = setInterval(() => { this.fetchComplaints(); }, 30000);
  },
  beforeDestroy() {
    clearInterval(this.polling);
  },
  methods: {
    // دالة مساعدة للقص (بدل الفلتر لتجنب المشاكل في القالب)
    truncate(text, length) {
        if (!text) return '';
        return text.length > length ? text.substring(0, length) + '...' : text;
    },
    // ... باقي الدوال (fetchComplaints, updateStatus, getRowClass, etc...)
    async fetchComplaints() {
      this.loading = true;
      try {
        const res = await api.get('/admin/complaints', { params: { status: this.filterStatus } });
        this.complaints = res.data.complaints.data;
        this.stats = res.data.stats;
      } catch (e) { console.error(e); } 
      finally { this.loading = false; }
    },
    async updateStatus(complaint, newStatus) {
      let responseText = null;
      if (newStatus === 'resolved') {
        responseText = prompt("هل تريد إضافة ملاحظة للحل؟ (اختياري)");
      }
      try {
        await api.post(`/admin/complaints/${complaint.id}/update`, {
          status: newStatus,
          admin_response: responseText
        });
        complaint.status = newStatus;
        if(responseText) complaint.admin_response = responseText;
        this.fetchComplaints(); 
      } catch (e) { alert("خطأ"); }
    },
    getRowClass(c) { return (c.priority === 'urgent' && c.status !== 'resolved') ? 'table-danger' : ''; },
    getPriorityClass(p) { return {'urgent': 'bg-danger','high': 'bg-warning text-dark','normal': 'bg-secondary'}[p]; },
    getPriorityText(p) { return {'urgent': 'عاجل جداً','high': 'هام','normal': 'عادي'}[p]; },
    getStatusBadge(s) { return {'sent': 'bg-danger','viewing': 'bg-primary','resolved': 'bg-success'}[s]; },
    getStatusText(s) { return {'sent': 'جديد','viewing': 'جاري المتابعة','resolved': 'تم الحل'}[s]; },
    getTypeText(t) { 
        const types = { 'administrative': 'إداري', 'security': 'أمني', 'medical': 'طبي', 'papers': 'أوراق', 'other': 'أخرى' };
        return types[t] || t; 
    },
    formatTime(d) { return new Date(d).toLocaleTimeString('ar-EG', {hour:'2-digit', minute:'2-digit'}); }
  }
};
</script>

<style scoped>
/* تنسيق خاص لصفوف الجدول الملونة */
.table-danger { background-color: #ffebe9 !important; }
.bg-danger-subtle { background-color: #ffebe9 !important; }

/* تحسينات للعرض */
.text-nowrap { white-space: nowrap; }
</style>